/**
 * A claim's progress, drawn as the design draws it.
 *
 * The steps come from the server: the shape is a rule about the status — a
 * refused claim's last step is a refusal, not a resolution — and two screens
 * deriving that independently is two chances to tell the customer the
 * opposite of the decision.
 *
 * An ordered list rather than a row of divs, because that is what it is: a
 * screen reader should hear four steps in order, and it should hear which one
 * the claim has reached.
 */

import { useTranslation } from "react-i18next";
import { moment } from "@hardauto/core-i18n";
import { StatusDot } from "@hardauto/core-ui";

import type { TimelineStep } from "./api";
import { NAMESPACE } from "./i18n";
import { stepTone } from "./status";
import "./screens/support.css";

export function Timeline({ steps }: { steps: readonly TimelineStep[] }) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const locale = i18n.language;

  return (
    // Named, so a screen reader announces "Suivi, liste de trois éléments"
    // rather than an unlabelled list among the page's others.
    <ol className="hardauto-support__timeline" aria-label={t("ticket.timeline")}>
      {steps.map((step) => (
        <li key={step.key} className="hardauto-support__step" data-state={step.state}>
          <StatusDot tone={stepTone(step.state)} label={t(`step.${step.key}`)} />
          <span className="hardauto-support__step-at">
            {/* An empty string, not an em dash: a step that has not happened
                has no date, and a dash under "Résolue" reads as a value. */}
            {step.at ? moment(step.at, locale) : ""}
          </span>
        </li>
      ))}
    </ol>
  );
}
